import { Link } from "react-router-dom";
import { ArrowRight, Wrench, Hammer } from "lucide-react";
import { PageHero, SectionHeading, Reveal, CTABand, SERVICE_ICONS, usePageMeta } from "@/components/kit";
import { SERVICES, HERO_BG, SLOGAN } from "@/lib/site";

export default function ServicesPage() {
  usePageMeta(
    "Security & Low-Voltage Service & Repair | Redeemer Solutions LLC",
    "Security camera, access control, fiber optics, Ethernet cabling, and video intercom service and repair in Christiana and Middle Tennessee."
  );
  return (
    <div data-testid="services-page">
      <PageHero
        testid="services-hero"
        eyebrow="Our Services"
        lines={["Serviced Right.", { t: "Repaired Fast.", gold: true }]}
        sub="Five specialties under one roof — every system diagnosed honestly, repaired fast, and maintained by the same accountable team."
        image={HERO_BG}
        imageAlt="Low-voltage and security technology services overview"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28 space-y-20">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[s.icon];
          const flip = i % 2 === 1;
          return (
            <Reveal key={s.slug}>
              <article
                data-testid={`services-row-${s.slug}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="relative overflow-hidden rounded-sm border border-gold/20">
                  <img src={s.image} alt={s.imageAlt} loading="lazy" className="w-full aspect-[16/11] object-cover opacity-85" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/70 via-transparent to-transparent" />
                  <div className="absolute top-5 left-5 w-11 h-11 flex items-center justify-center border border-gold/40 bg-[#0A0A0C]/80 backdrop-blur-sm">
                    <Icon size={20} className="text-gold" />
                  </div>
                </div>
                <div>
                  <p className="font-mono-label text-xs uppercase tracking-[0.28em] text-gold">{s.tagline}</p>
                  <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#F4EFE6] mt-4 leading-tight">{s.name}</h2>
                  <p className="text-zinc-400 text-base leading-relaxed mt-4">{s.short}</p>
                  <div className="flex flex-wrap gap-x-8 gap-y-2 mt-6 text-sm text-zinc-300">
                    <span className="inline-flex items-center gap-2"><Hammer size={14} className="text-gold" /> Service & Maintenance</span>
                    <span className="inline-flex items-center gap-2"><Wrench size={14} className="text-gold" /> Repair & Restoration</span>
                  </div>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`services-explore-${s.slug}`}
                    className="inline-flex items-center gap-2 text-gold text-xs font-mono-label uppercase tracking-[0.22em] mt-7 hover:gap-3.5 transition-all"
                  >
                    Explore This Service <ArrowRight size={14} />
                  </Link>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24">
        <SectionHeading
          eyebrow="Not Sure Where to Start?"
          title="Tell us the problem — we'll bring the solution."
          sub="Every call starts with honest diagnostics. We find the real fault, explain your options in plain language, and quote fairly."
          align="center"
          testid="services-start-heading"
        />
      </section>

      <CTABand title={SLOGAN} />
    </div>
  );
}
